// Generic click-to-edit inline field
// Shows display text by default, reveals input on click

import { useState, useRef, useEffect } from 'react';
import { normalizeSocialUrl } from '@startus/lib';

interface InlineFieldProps {
  value: string;
  displayValue?: string; // Override display text (e.g. formatted date)
  placeholder?: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date' | 'url';
  label?: string;
  className?: string;
  textSize?: string; // Tailwind text size class(es), e.g. 'text-sm md:text-base'
  min?: string; // HTML min attribute (e.g. min date for date inputs)
  max?: string; // HTML max attribute (e.g. max date for date inputs)
}

export function InlineField({
  value,
  displayValue,
  placeholder = '',
  onChange,
  type = 'text',
  label,
  className = '',
  textSize = 'text-sm',
  min,
  max,
}: InlineFieldProps) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'url') {
      const normalized = normalizeSocialUrl(e.target.value);
      if (normalized !== e.target.value) onChange(normalized);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') setEditing(false);
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {label && <span className="text-sm text-[#EAEAEA] min-w-30">{label}</span>}
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          min={min}
          max={max}
          className="input input-sm input-bordered flex-1"
        />
      </div>
    );
  }

  const text = displayValue || value;

  return (
    <div
      className={`flex items-center justify-between gap-2 cursor-pointer hover:bg-base-200 rounded px-1 py-0.5 ${className}`}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}
      role="button"
      tabIndex={0}
    >
      {label ? (
        // Labeled layout: label left | value right (primary blue when filled)
        <>
          <span className={`${textSize} text-[#EAEAEA] min-w-30 shrink-0`}>{label}</span>
          <span className={`text-sm ${text ? 'text-[#19A2F8]' : 'text-[#EAEAEA]/40'}`}>
            {text || placeholder}
          </span>
        </>
      ) : (
        // Content layout: [icon? + value] left | [placeholder hint] right
        <>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {type === 'url' && (
              <svg
                className="w-5 h-5 text-[#EAEAEA] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  d="M9.33333 7.99777C9.33333 6.33662 7.78869 4.66602 5.90476 4.66602C5.6816 4.66602 4.94602 4.66602 4.7619 4.66602C2.86835 4.66602 1.33333 6.15769 1.33333 7.99777C1.33333 9.58337 2.47315 10.9103 3.99999 11.247C4.24503 11.301 4.50004 11.3295 4.7619 11.3295"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66667 7.99833C6.66667 9.65948 8.21131 11.3301 10.0952 11.3301C10.3184 11.3301 11.054 11.3301 11.2381 11.3301C13.1316 11.3301 14.6667 9.8384 14.6667 7.99833C14.6667 6.41272 13.5269 5.08581 12 4.74914C11.755 4.69511 11.5 4.66658 11.2381 4.66658"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span
              className={`${textSize} truncate ${text ? 'text-[#EAEAEA]' : 'text-[#EAEAEA]/40'} ${type === 'url' && text ? 'underline underline-offset-6' : ''}`}
            >
              {text || placeholder}
            </span>
          </div>
          {/* Show placeholder as click-to-edit hint when field has a value */}
          {text && placeholder && (
            <span className="text-sm text-[#EAEAEA]/40 shrink-0">{placeholder}</span>
          )}
        </>
      )}
    </div>
  );
}
